import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import Button from './Button';
import GoogleMaterialIcon from '../GoogleMaterialIcon';

export interface FloatingAction {
  label: string;
  icon: string;
  onClick: () => void;
  variant?: 'primary' | 'outline' | 'destructive' | 'ghost' | 'danger';
  disabled?: boolean;
  // Hover tooltip shown on the button. When the button is disabled, surfaces
  // the reason — otherwise the user just sees a grey button with no clue why.
  tooltip?: string;
  type?: 'button' | 'submit' | 'reset';
  className?: string;
}

interface FloatingActionsProps {
  actions: FloatingAction[];
}

/**
 * FloatingActions Component
 * 
 * Provides a responsive set of actions that float above content.
 * - Desktop (lg+): Centered horizontal bar at the bottom.
 * - Mobile (<lg): Circular FAB (single action) or Ellipsis FAB (multiple actions).
 */
const FloatingActions: React.FC<FloatingActionsProps> = ({ actions }) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted || actions.length === 0) return null;

  // Desktop View: Individual circle FABs in a row
  const DesktopView = (
    <div className="hidden md:flex fixed bottom-6 left-1/2 -translate-x-1/2 z-[2000000] gap-6 items-center">
      {actions.map((action, index) => (
        <motion.button
          key={index}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.1 }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          onClick={action.onClick}
          disabled={action.disabled}
          type={action.type || 'button'}
          className={`h-14 px-6 rounded-full shadow-2xl flex items-center justify-center gap-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
            action.variant === 'destructive' || action.variant === 'danger'
              ? 'bg-red-600 text-white'
              : action.variant === 'outline'
              ? 'bg-white text-gray-700 border border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700'
              : 'bg-brand-gradient text-white'
          } ${action.className || ''}`}
          title={action.tooltip || action.label}
        >
          <GoogleMaterialIcon name={action.icon} size="md" />
          <span className="text-sm font-semibold whitespace-nowrap">{action.label}</span>
        </motion.button>
      ))}
    </div>
  );

  // Mobile View: Side-by-side FABs (Centered bottom)
  const MobileView = (
    <div className="md:hidden fixed bottom-6 left-1/2 -translate-x-1/2 z-[2000000] flex gap-4 items-center">
      {actions.map((action, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, y: 20, scale: 0.8 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ 
            duration: 0.2, 
            delay: index * 0.05,
            ease: "easeOut"
          }}
          className="flex flex-col items-center gap-1.5"
        >
          <button
            onClick={action.onClick}
            disabled={action.disabled}
            type={action.type || 'button'}
            title={action.tooltip || action.label}
            className={`w-14 h-14 rounded-full shadow-2xl flex items-center justify-center transition-all active:scale-90 ${
              action.variant === 'primary'
                ? 'bg-brand-gradient text-white shadow-brand-500/25'
                : action.variant === 'destructive' || action.variant === 'danger'
                ? 'bg-red-600 text-white shadow-red-500/25'
                : 'bg-white text-gray-700 dark:bg-gray-800 dark:text-gray-200 border border-gray-200 dark:border-gray-700'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            <GoogleMaterialIcon name={action.icon} size="md" />
          </button>
          <motion.span 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-center max-w-[80px] truncate"
          >
            {action.label}
          </motion.span>
        </motion.div>
      ))}
    </div>
  );

  return createPortal(
    <>
      {DesktopView}
      {MobileView}
    </>,
    document.body
  );
};

export default FloatingActions;
